import React, { useEffect, useRef, useState } from 'react';
import { Boxes, Package, AlertTriangle, ArrowRight } from 'lucide-react';

const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// Count from 0 -> target once, on mount. Skips the animation under reduced-motion.
function useCountUp(target, duration = 1400) {
  const [value, setValue] = useState(prefersReducedMotion() ? target : 0);
  const raf = useRef(null);
  useEffect(() => {
    if (prefersReducedMotion()) {
      setValue(target);
      return undefined;
    }
    let start = null;
    const tick = (ts) => {
      if (start === null) start = ts;
      const p = Math.min((ts - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setValue(Math.round(target * eased));
      if (p < 1) raf.current = window.requestAnimationFrame(tick);
    };
    raf.current = window.requestAnimationFrame(tick);
    return () => raf.current && window.cancelAnimationFrame(raf.current);
  }, [target, duration]);
  return value;
}

const STATS = [
  { key: 'stock', icon: Boxes, label: 'Stock on hand', target: 12480, suffix: ' kg', trend: '+6.2%' },
  { key: 'orders', icon: Package, label: 'Open orders', target: 37, suffix: '', trend: '+4' },
  { key: 'alerts', icon: AlertTriangle, label: 'Low-stock alerts', target: 4, suffix: '', trend: 'watch', warn: true }
];

const BARS = [42, 58, 47, 65, 54, 72, 61];

// The pipeline rotates through industries so the mockup speaks to every sector,
// not just one. Each entry = one order flowing through that industry's stages.
const SCENARIOS = [
  { industry: 'Textiles', product: 'Cotton Yarn 30s', qty: '480 kg', stages: ['Dyeing', 'Winding', 'Packing'] },
  { industry: 'Foam', product: 'PU Foam Block', qty: '40 blocks', stages: ['Mixing', 'Foaming', 'Curing'] },
  { industry: 'Food & Bev', product: 'Mango Pulp', qty: '60 cases', stages: ['Blending', 'Filling', 'Sealing'] },
  { industry: 'Apparel', product: 'Crew T-Shirt', qty: '900 pcs', stages: ['Cutting', 'Stitching', 'Finishing'] },
  { industry: 'Pharma', product: 'Paracetamol 500', qty: '2,400 strips', stages: ['Granulation', 'Compression', 'Blister'] }
];

function StatCard({ stat }) {
  const value = useCountUp(stat.target);
  const Icon = stat.icon;
  return (
    <div className={`hm-stat${stat.warn ? ' hm-stat-warn' : ''}`}>
      <div className="hm-stat-top">
        <span className="hm-stat-icon"><Icon size={14} /></span>
        <span className={`hm-stat-trend${stat.warn ? ' warn' : ''}`}>{stat.trend}</span>
      </div>
      <div className="hm-stat-value">
        {value.toLocaleString('en-IN')}<span className="hm-stat-suffix">{stat.suffix}</span>
      </div>
      <div className="hm-stat-label">{stat.label}</div>
    </div>
  );
}

// Decorative product preview — not read by assistive tech.
export default function HeroAppMockup() {
  const [idx, setIdx] = useState(0);
  const sc = SCENARIOS[idx];
  // Advance to the next industry each time the flow card completes a loop
  // (the card is invisible at the loop boundary, so the swap is seamless).
  const onFlowLoop = (e) => {
    if (e.animationName === 'hm-flow-move') setIdx((i) => (i + 1) % SCENARIOS.length);
  };
  return (
    <div className="hero-mockup" aria-hidden="true">
      <div className="hm-window">
        <div className="hm-titlebar">
          <span className="hm-dots"><i /><i /><i /></span>
          <span className="hm-title">The Inventory Studio</span>
          <span className="hm-live"><i className="hm-live-dot" /> Live</span>
        </div>

        <div className="hm-body">
          <div className="hm-stats">
            {STATS.map((s) => <StatCard key={s.key} stat={s} />)}
          </div>

          <div className="hm-board">
            <div className="hm-board-head">
              <span>Production pipeline</span>
              <span className="hm-board-industry" key={idx}>
                <span className="hm-ind-dot" /> {sc.industry}
              </span>
            </div>
            <div className="hm-columns">
              {sc.stages.map((stage, i) => (
                <div className="hm-col" key={i}>
                  <div className="hm-col-title" key={`${idx}-${i}`}>{stage}</div>
                  <div className="hm-slot" />
                </div>
              ))}
              {/* the order card that flows across the three stages on a loop,
                  swapping to the next industry each time it completes */}
              <div className="hm-flow" onAnimationIteration={onFlowLoop}>
                <div className="hm-flow-card">
                  <span className="hm-flow-name">{sc.product}</span>
                  <span className="hm-flow-meta">{sc.qty} <ArrowRight size={11} /></span>
                </div>
              </div>
            </div>
          </div>

          <div className="hm-chart">
            <div className="hm-chart-head">Weekly output</div>
            <div className="hm-bars">
              {BARS.map((h, i) => (
                <span key={i} className="hm-bar" style={{ '--h': `${h}%`, '--i': i }} />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
